<template>
    <div class="block-fan-images" ref="fanImagesContainer">
        <!-- Background image -->
        <div
            class="bgimg parallax-hook"
            :style="`background-image:url('${window.config.json.artifacts.timelineBg}')`"
        ></div>
        <div class="capper">
            <div class="capper-padded">
                <!-- Image rows -->
                <div class="fan-rows">
                    <div
                        v-for="(row, rowIndex) in imageRows"
                        :key="`row-${rowIndex}`"
                        class="fan-row"
                        :style="{ height: rowHeight + 'px' }"
                        ref="rows"
                    >
                        <div
                            v-for="item in row"
                            :key="item.id"
                            class="fan-image"
                            :class="`fan-image-${item.id}`"
                            @click="handleClick(item)"
                        >
                            <template v-if="item.isCorner">
                                <FanImages_Corner :json="json" />
                            </template>
                            <template v-else>
                                <div class="fanartimg imgover">
                                    <img :src="item.url" :alt="item.title" />
                                    <div class="plus-circle"><span>+</span></div>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="left-corner">
            <TtmotifIcon class="tt-motif-svg" aria-label="Insomniac" />
        </div>
        <div class="arrows">
            <div
                class="swiper-button swiper-button-prev"
                :class="{ disabled: isFirstPage }"
                @click="prevSlide"
            ></div>
            <div
                class="swiper-button swiper-button-next"
                :class="{ disabled: isLastPage }"
                @click="nextSlide"
            ></div>
        </div>
    </div>
</template>

<script>
import gsap from '@/plugins/gsap'
import FanImages_Corner from '@/components/blocks/FanImages_Corner'
import { isMobile } from 'mobile-device-detect'
import TtmotifIcon from '@/components/svgs/Ttmotif.vue'

export default {
    name: 'FanImages',
    components: { TtmotifIcon, FanImages_Corner },
    props: {
        json: { type: Object, required: true }
    },
    data() {
        return {
            window: window,
            currentPage: 0,
            totalPages: 0,
            pageWidth: window.innerWidth,
            windowWidth: window.innerWidth,
            windowHeight: window.innerHeight,
            rowGap: 50,
            topPadding: 100,
            bottomPadding: 150,
            resizeTimeout: null,
            touchStartX: 0,
            touchEndX: 0,
            isMouseDown: false,
            mouseStartX: 0,
            mouseEndX: 0
        }
    },
    computed: {
        numRows() {
            return this.windowWidth < 800 ? 3 : 2
        },
        allImages() {
            // give each image a stable, unique ID matching its index in this array
            const corner = { isCorner: true, id: 0 }
            const rest = this.json.fanImages.map((img, i) => ({
                ...img,
                id: i + 1
            }))
            return [corner, ...rest]
        },
        allImagesNoCorner() {
            const rest = this.json.fanImages.map((img, i) => ({
                ...img,
                id: i + 1
            }))
            return rest
        },
        imageRows() {
            const rows = Array.from({ length: this.numRows }, () => [])
            this.allImages.forEach((item, idx) => {
                rows[idx % this.numRows].push(item)
            })
            return rows
        },
        rowHeight() {
            const totalVertical = this.topPadding + this.bottomPadding
            const totalGap = (this.numRows - 1) * this.rowGap
            return (this.windowHeight - totalVertical - totalGap) / this.numRows
        },
        isFirstPage() {
            return this.currentPage === 0
        },
        isLastPage() {
            return this.currentPage >= this.totalPages - 1
        }
    },
    methods: {
        handleClick(item) {
            if (item.isCorner) return
            // use the unique id (which matches its index in allImages)
            this.openSlideshow(item.id)
        },
        updateDimensions() {
            this.windowWidth = window.innerWidth
            this.windowHeight = window.innerHeight
            this.pageWidth = window.innerWidth

            const rows = Array.isArray(this.$refs.rows)
                ? this.$refs.rows
                : [this.$refs.rows]
            rows.forEach(row => {
                if (row) {
                    const imgs = row.querySelectorAll('.fan-image')
                    gsap.set(imgs, { x: 0 })
                }
            })

            this.currentPage = 0
            this.$nextTick(() => {
                setTimeout(() => {
                    let maxW = 0
                    rows.forEach(row => {
                        if (row) maxW = Math.max(maxW, row.scrollWidth)
                    })
                    this.totalPages = Math.ceil(maxW / this.pageWidth)
                }, 100)
            })
        },
        onResize() {
            clearTimeout(this.resizeTimeout)
            this.resizeTimeout = setTimeout(this.updateDimensions, 100)
        },
        prevSlide() {
            if (this.isFirstPage) return
            const np = this.currentPage - 1
            this.animatePageTransition(np, 'prev')
            this.currentPage = np
        },
        nextSlide() {
            if (this.isLastPage) return
            const np = this.currentPage + 1
            this.animatePageTransition(np, 'next')
            this.currentPage = np
        },
        animatePageTransition(newPage, direction) {
            const offset = -newPage * this.pageWidth
            const rows = Array.isArray(this.$refs.rows)
                ? this.$refs.rows
                : [this.$refs.rows]
            rows.forEach(row => {
                const imgs = row.querySelectorAll('.fan-image')
                gsap.to(imgs, {
                    x: offset,
                    duration: 0.8,
                    ease: 'power2.inOut',
                    stagger: {
                        amount: 0.3,
                        from: direction === 'next' ? 'start' : 'end'
                    }
                })
            })
        },
        initAnimation() {
            const rows = Array.isArray(this.$refs.rows)
                ? this.$refs.rows
                : [this.$refs.rows]
            rows.forEach(row => {
                const imgs = row.querySelectorAll('.fan-image')
                gsap.fromTo(
                    imgs,
                    { x: window.innerWidth, opacity: 0 },
                    { x: 0, opacity: 1, stagger: 0.1, duration: 0.5, ease: 'power2.out' }
                )
            })
        },
        openSlideshow(id) {
            // find the index in allImages where item.id === id
            const index = this.allImagesNoCorner.findIndex(item => item.id === id)

            this.$store.commit('openPopup', {
                gallery: true,
                startingIndex: index,
                items: this.allImagesNoCorner
            })
        },
        handleImageLoad() {
            this.updateDimensions()
        },
        handleTouchStart(e) {
            this.touchStartX = e.changedTouches[0].screenX
        },
        handleTouchEnd(e) {
            this.touchEndX = e.changedTouches[0].screenX
            this.handleSwipeGesture(this.touchStartX, this.touchEndX)
        },
        handleMouseDown(e) {
            this.isMouseDown = true
            this.mouseStartX = e.screenX
            e.preventDefault()
        },
        handleMouseMove(e) {
            if (this.isMouseDown) e.preventDefault()
        },
        handleMouseUp(e) {
            if (!this.isMouseDown) return
            this.isMouseDown = false
            this.mouseEndX = e.screenX
            this.handleSwipeGesture(this.mouseStartX, this.mouseEndX)
        },
        handleSwipeGesture(startX, endX) {
            const diff = startX - endX
            if (Math.abs(diff) > 50) {
                diff > 0 ? this.nextSlide() : this.prevSlide()
            }
        }
    },
    mounted() {
        if (!isMobile) window.addEventListener('resize', this.onResize)
        const container = this.$refs.fanImagesContainer
        container.addEventListener('touchstart', this.handleTouchStart)
        container.addEventListener('touchend', this.handleTouchEnd)
        container.addEventListener('mousedown', this.handleMouseDown)
        container.addEventListener('mousemove', this.handleMouseMove)
        container.addEventListener('mouseup', this.handleMouseUp)
        container.addEventListener('mouseleave', this.handleMouseUp)

        setTimeout(this.updateDimensions, 300)

        const rows = Array.isArray(this.$refs.rows)
            ? this.$refs.rows
            : [this.$refs.rows]
        rows.forEach(row => {
            row.querySelectorAll('img').forEach(img =>
                img.addEventListener('load', this.handleImageLoad)
            )
            gsap.set(row.querySelectorAll('.fan-image'), {
                x: window.innerWidth,
                opacity: 0
            })
        })

        const observer = new IntersectionObserver((entries, obs) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && entry.intersectionRatio >= 0.45) {
                    this.initAnimation()
                    obs.unobserve(entry.target)
                }
            })
        }, { threshold: [0.45] })

        observer.observe(this.$el)
    },
    beforeUnmount() {
        if (!isMobile) window.removeEventListener('resize', this.onResize)
        const container = this.$refs.fanImagesContainer
        container.removeEventListener('touchstart', this.handleTouchStart)
        container.removeEventListener('touchend', this.handleTouchEnd)
        container.removeEventListener('mousedown', this.handleMouseDown)
        container.removeEventListener('mousemove', this.handleMouseMove)
        container.removeEventListener('mouseup', this.handleMouseUp)
        container.removeEventListener('mouseleave', this.handleMouseUp)

        const rows = Array.isArray(this.$refs.rows)
            ? this.$refs.rows
            : [this.$refs.rows]
        rows.forEach(row => {
            row.querySelectorAll('img').forEach(img =>
                img.removeEventListener('load', this.handleImageLoad)
            )
        })
    }
}
</script>